import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const PrivacyPolicyPage: React.FC = () => {
  const sections = [
    {
      title: 'Information We Collect',
      content: [
        'Personal Information: Name, email address, phone number, and company details when you contact us or request services.',
        'Technical Information: IP addresses, browser type, device information, and usage patterns when you visit our website.',
        'Communication Data: Records of communications with our team, including emails, chat logs, and support tickets.',
        'Service Data: Information related to the services we provide, including project details and deliverables.',
      ]
    },
    {
      title: 'How We Use Your Information',
      content: [
        'Service Delivery: To provide, maintain, and improve our development and consulting services.',
        'Communication: To respond to inquiries, provide customer support, and send service-related notifications.',
        'Business Operations: To manage our business operations, including billing, accounting, and legal compliance.',
        'Website Improvement: To analyze website usage and improve user experience.',
        'Marketing: With your consent, to send promotional materials about our services.',
      ]
    },
    {
      title: 'Information Sharing and Disclosure',
      content: [
        'We do not sell, trade, or rent your personal information to third parties.',
        'Service Providers: We may share information with trusted service providers who assist in our operations.',
        'Third-Party AI Services: For app features that use artificial intelligence, we may share only the data needed to provide the selected feature with the AI providers identified in this policy, after obtaining the user consent required by the app.',
        'Legal Requirements: We may disclose information when required by law or to protect our rights.',
        'Business Transfers: Information may be transferred in connection with mergers or acquisitions.',
      ]
    },
    {
      title: 'Data Security',
      content: [
        'We implement industry-standard security measures to protect your information.',
        'All data transmissions are encrypted using SSL/TLS technology.',
        'Access to personal information is restricted to authorized personnel only.',
        'We regularly review and update our security practices.',
      ]
    },
    {
      title: 'Data Retention',
      content: [
        'We retain personal information only as long as necessary for the purposes outlined in this policy.',
        'Service-related data is typically retained for the duration of our business relationship.',
        'Marketing data is retained until you opt out or request deletion.',
        'Legal compliance may require longer retention periods.',
      ]
    },
    {
      title: 'Your Rights',
      content: [
        'Access: You can request access to your personal information.',
        'Correction: You can request correction of inaccurate information.',
        'Deletion: You can request deletion of your personal information, subject to legal requirements.',
        'Portability: You can request a copy of your data in a portable format.',
        'Opt-out: You can opt out of marketing communications at any time.',
      ]
    },
    {
      title: 'Cookies and Tracking',
      content: [
        'We use cookies to improve website functionality and user experience.',
        'Analytics cookies help us understand how visitors use our website.',
        'You can control cookie settings through your browser preferences.',
        'Some website features may not function properly if cookies are disabled.',
      ]
    },
    {
      title: 'International Data Transfers',
      content: [
        'Your information may be transferred to and processed in countries other than your own.',
        'We ensure appropriate safeguards are in place for international transfers.',
        'Data transfers comply with applicable privacy laws and regulations.',
      ]
    },
    {
      title: 'Children\'s Privacy',
      content: [
        'Our services are not intended for children under 13 years of age.',
        'We do not knowingly collect personal information from children.',
        'If we become aware of such collection, we will promptly delete the information.',
      ]
    },
    {
      title: 'Use of Microsoft Clarity and Microsoft Advertising',
      content: [
        'We partner with Microsoft Clarity and Microsoft Advertising to capture how you use and interact with our website through behavioral metrics, heatmaps, and session replay to improve and market our products and services.',
        'How the technology works: Microsoft Clarity collects aggregated behavioral metrics (such as page views, click counts, and scroll depth), generates heatmaps that show areas of high interaction, and records session replays which may include mouse movements, clicks, scrolling, and other on-page interactions. Clarity and Microsoft Advertising also collect technical data like device and browser information, IP addresses, referrer URLs, and pages visited to help analyze and improve site performance and security.',
        'Purposes: We use these tools to analyze usability, diagnose and fix issues, optimize content and performance, detect and prevent fraudulent activity, and to support our advertising and marketing efforts.',
        'For more information about how Microsoft collects and uses your data, visit the Microsoft Privacy Statement: https://privacy.microsoft.com/privacystatement. For product-specific information about Clarity, see https://clarity.microsoft.com.'
      ]
    },
    {
      title: 'Spendifly App - Receipt Data and AI Processing',
      content: [
        'Spendifly is our receipt and expense management app. When you use Spendifly, we may collect account information, team and business settings, subscription status, receipt images, extracted receipt text, receipt fields such as merchant, date, totals, taxes, currency, category, line items, payment method, tags, comments, approval status, and related user, team, and receipt identifiers.',
        'How We Collect Spendifly Data: This information is collected when you create or sign in to an account, set up a team or business profile, capture or upload a receipt, edit receipt details, connect supported integrations, use subscription features, contact support, or when Spendifly generates OCR text and structured receipt fields from a receipt you choose to scan.',
        'How We Use Spendifly Data: We use this data to authenticate your account, save and sync receipts, organize expenses for your team, generate reports and exports, provide customer support, maintain security, comply with legal obligations, and provide optional AI-powered receipt scanning and analysis.',
        'Google Cloud Vision OCR: If you allow AI receipt processing in the app, receipt images may be sent to Google Cloud Vision through Firebase Cloud Functions to extract text from the receipt image.',
        'Google Gemini AI: If you allow AI receipt processing in the app, extracted receipt text and related processing context may be sent to Google Gemini AI through Firebase Cloud Functions to identify merchant, date, totals, taxes, currency, category, payment method, and line items. Account, team, and receipt identifiers may be used to authenticate the request, store the result in your account, and maintain processing status.',
        'User Permission and Control: Spendifly asks for your permission before sending receipt images or extracted receipt text to third-party AI services. If you choose not to allow AI processing, Spendifly will not send your receipt image or extracted receipt text to Google Cloud Vision or Google Gemini AI for that AI processing flow. You can manage or revoke this permission in Spendifly Settings under AI Data Processing Consent.',
        'Third-Party Protection: We use Google Cloud, Firebase, Google Cloud Vision, and Google Gemini AI as service providers for Spendifly. We require service providers that process personal data for us to provide privacy, confidentiality, and security protections that are the same or equal to the protections described in this policy and to process data only for the purposes we authorize.',
        'Retention and Deletion: Spendifly receipt data is retained while your account or team needs it for receipt history, reporting, legal compliance, or support. You can delete receipts in the app, use the in-app account deletion option, or contact us at gamiflytech@gmail.com for help with data access or deletion.',
        'We do not sell Spendifly receipt data, extracted receipt text, or AI processing results, and we do not use Spendifly receipt content for third-party advertising.',
      ]
    },
    {
      title: 'Aiko App - Third-Party AI Services',
      content: [
        'Our mobile application "Aiko - Your AI Soulmate" uses third-party artificial intelligence (AI) services to provide personalized conversational experiences.',
        'Data Sent to AI Services: When you use chat features in the Aiko app, the following information may be transmitted to our AI service provider: (1) Your chat messages and conversation history necessary to generate contextual AI responses, (2) Your nickname and basic profile preferences to personalize conversations, (3) Conversation context such as relationship settings and character preferences.',
        'AI Service Provider: We use DeepSeek AI (https://deepseek.com) as our primary AI service provider to process chat messages and generate AI responses. DeepSeek processes your data in accordance with their privacy policy.',
        'Purpose of Data Processing: Your data is shared with DeepSeek AI solely to generate personalized AI responses and improve your conversational experience within the app. We do not use this data for advertising purposes.',
        'User Consent: The Aiko app requests your explicit consent before sending any data to AI services. You can review and accept the data sharing terms when you first use the app. If you do not consent, you will not be able to use the AI chat features.',
        'Data Retention by AI Provider: DeepSeek AI may retain conversation data as described in their privacy policy. We recommend reviewing their privacy practices at https://deepseek.com for detailed information.',
        'Your Control: You can delete your conversation history within the app at any time. For complete data deletion, please contact us at gamiflytech@gmail.com.',
      ]
    },
    {
      title: 'Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time.',
        'Material changes will be notified through our website or email.',
        'Continued use of our services constitutes acceptance of the updated policy.',
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Header Section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #4A90E2 0%, #5C6BC0 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            GamiFly Tech - Your Privacy Matters
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Last Updated: May 2026"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip 
              label="GDPR Compliant" 
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        </Paper>

        {/* Introduction */}
        <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Introduction
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            At GamiFly Tech, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our services.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            By using our website or services, you agree to the collection and use of information in accordance with this policy. 
            If you do not agree with our policies and practices, please do not use our services.
          </Typography>
          <Box sx={{ p: 3, backgroundColor: 'primary.light', borderRadius: 2, color: 'white' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Contact Information: If you have questions about this Privacy Policy, please contact us at gamiflytech@gmail.com
            </Typography>
          </Box>
        </Paper>

        {/* Main Content Sections */}
        {sections.map((section, index) => (
          <Paper key={index} elevation={1} sx={{ p: 4, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
              {index + 1}. {section.title}
            </Typography>
            <List sx={{ pl: 0 }}>
              {section.content.map((item, itemIndex) => (
                <ListItem key={itemIndex} sx={{ pl: 0, pb: 1 }}>
                  <ListItemText
                    primary={item}
                    sx={{
                      '& .MuiListItemText-primary': {
                        lineHeight: 1.7,
                        fontSize: '1rem',
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            {index < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Paper>
        ))}

        

        {/* Contact Section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mt: 4,
            background: 'linear-gradient(135deg, #E84393 0%, #FD79A8 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Questions or Concerns?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            If you have any questions about this Privacy Policy or how we handle your personal information, 
            we encourage you to contact us. We are committed to resolving any concerns promptly and transparently.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Email</Typography>
              <Typography variant="body2">gamiflytech@gmail.com</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Response Time</Typography>
              <Typography variant="body2">Within 48 hours</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Data Protection Officer</Typography>
              <Typography variant="body2">Available upon request</Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default PrivacyPolicyPage;
